import * as THREE from 'three'
import {
  GltfDirectBuildError,
  GltfLowMemoryUnsupportedError,
  GltfMemoryBudgetExceededError,
  MeshLoadCancelledError,
} from '@/plugins/mesh/errors'
import { decodeDataUri } from '@/plugins/mesh/loaders/gltfLowMemory/decodeDataUri'
import {
  type EncodedImageDimensions,
  readEncodedImageDimensions,
} from '@/plugins/mesh/loaders/gltfLowMemory/encodedImageDimensions'
import { estimateGltfTextureBytes } from '@/plugins/mesh/loaders/gltfLowMemory/estimateTextureBytes'
import { getConcreteGltfTextureMaxSize } from '@/plugins/mesh/loaders/gltfLowMemory/gltfTextureAutoSize'
import { ImageBitmapLifetime } from '@/plugins/mesh/loaders/gltfLowMemory/imageBitmapLifetime'
import type {
  GltfBufferSource,
  GltfCoreMaterialFactory,
  GltfImageDecoder,
  GltfImageDecoderPerformanceReport,
  GltfMaterialPerformanceReport,
} from '@/plugins/mesh/loaders/gltfLowMemory/interfaces'
import { resolveGltfLoadingOptions } from '@/plugins/mesh/loaders/gltfLowMemory/lowMemoryOptions'
import type {
  GltfImage,
  GltfManifestDocument,
  GltfMaterial,
  GltfMaterialFeatures,
  GltfTextureInfo,
} from '@/plugins/mesh/loaders/gltfLowMemory/types'
import type { GltfLoadingOptions, MeshLoadRequest } from '@/plugins/mesh/types'
import { getLoggerManager, LogLevel } from '@/shared/logging'

const clock = getLoggerManager()
const textureLog = getLoggerManager().getLogger('mesh:gltf:texture')

function percentile(values: readonly number[], ratio: number): number {
  if (values.length === 0) return 0
  const sorted = [...values].sort((left, right) => left - right)
  return sorted[Math.min(sorted.length - 1, Math.floor((sorted.length - 1) * ratio))] ?? 0
}

function throwIfCancelled(signal: AbortSignal | undefined, source: string): void {
  if (signal?.aborted) {
    throw new MeshLoadCancelledError(source, { cause: signal.reason })
  }
}

function dataUriToBlob(uri: string): Blob {
  const { bytes, mimeType } = decodeDataUri(uri)
  return new Blob([bytes.slice().buffer as ArrayBuffer], { type: mimeType })
}

function resolveImageUrl(uri: string, baseUrl: string): string {
  if (baseUrl) return new URL(uri, baseUrl).href
  return uri
}

function getWrap(value: number | undefined): THREE.Wrapping {
  if (value === 33071) return THREE.ClampToEdgeWrapping
  if (value === 33648) return THREE.MirroredRepeatWrapping
  return THREE.RepeatWrapping
}

function getMagFilter(value: number | undefined): THREE.MagnificationTextureFilter {
  return value === 9728 ? THREE.NearestFilter : THREE.LinearFilter
}

function getMinFilter(value: number | undefined): THREE.MinificationTextureFilter {
  switch (value) {
    case 9728:
      return THREE.NearestFilter
    case 9729:
      return THREE.LinearFilter
    case 9984:
      return THREE.NearestMipmapNearestFilter
    case 9985:
      return THREE.LinearMipmapNearestFilter
    case 9986:
      return THREE.NearestMipmapLinearFilter
    default:
      return THREE.LinearMipmapLinearFilter
  }
}

function getResizedDimensions(
  width: number,
  height: number,
  maxSize: number
): readonly [number, number] | undefined {
  if (!Number.isFinite(maxSize) || maxSize <= 0 || Math.max(width, height) <= maxSize) {
    return undefined
  }
  const scale = maxSize / Math.max(width, height)
  return [Math.max(1, Math.round(width * scale)), Math.max(1, Math.round(height * scale))]
}

const IMAGE_BITMAP_OPTIONS: ImageBitmapOptions = {
  imageOrientation: 'none',
  premultiplyAlpha: 'none',
  colorSpaceConversion: 'none',
}

function getImageDimensions(image: TexImageSource): readonly [number, number] {
  const sized = image as TexImageSource & {
    readonly naturalWidth?: number
    readonly naturalHeight?: number
    readonly width?: number
    readonly height?: number
  }
  return [sized.naturalWidth ?? sized.width ?? 0, sized.naturalHeight ?? sized.height ?? 0]
}

function assertSupportedMaterialExtensions(
  material: GltfMaterial | undefined,
  materialIndex: number | undefined,
  source: string
): void {
  const extensions = Object.keys(material?.extensions ?? {}).filter(
    (extension) => extension !== 'KHR_materials_unlit'
  )
  if (extensions.length > 0) {
    throw new GltfLowMemoryUnsupportedError(
      source,
      'unsupported-material-extension',
      `materials[${materialIndex ?? 'default'}]:${extensions.join(',')}`
    )
  }
  const textureInfos: (GltfTextureInfo | undefined)[] = [
    material?.pbrMetallicRoughness?.baseColorTexture,
    material?.pbrMetallicRoughness?.metallicRoughnessTexture,
    material?.normalTexture,
    material?.occlusionTexture,
    material?.emissiveTexture,
  ]
  for (const info of textureInfos) {
    const textureExtensions = Object.keys(info?.extensions ?? {})
    if (textureExtensions.length > 0) {
      throw new GltfLowMemoryUnsupportedError(
        source,
        'unsupported-material-extension',
        textureExtensions.join(',')
      )
    }
  }
}

interface GltfDocumentMaterialCaches {
  readonly materials: Map<string, Promise<THREE.MeshStandardMaterial | THREE.MeshBasicMaterial>>
  readonly textures: Map<string, Promise<THREE.Texture>>
  readonly images: Map<number, Promise<TexImageSource>>
  estimatedTextureBytes: number
}

function createDocumentCaches(): GltfDocumentMaterialCaches {
  return {
    materials: new Map(),
    textures: new Map(),
    images: new Map(),
    estimatedTextureBytes: 0,
  }
}

/** 使用 createImageBitmap 或 HTMLImageElement 的默认可取消图片解码器。 */
export class BrowserGltfImageDecoder implements GltfImageDecoder {
  private headerMs = 0
  private directDecodeMs = 0
  private fullDecodeMs = 0
  private resizeMs = 0
  private directResizeCount = 0
  private fallbackResizeCount = 0
  private unchangedCount = 0

  constructor(private readonly maxSize = Number.POSITIVE_INFINITY) {}

  getPerformanceReport(): GltfImageDecoderPerformanceReport {
    return {
      decoderKind: typeof createImageBitmap === 'function' ? 'image-bitmap' : 'html-image',
      headerMs: this.headerMs,
      directDecodeMs: this.directDecodeMs,
      fullDecodeMs: this.fullDecodeMs,
      resizeMs: this.resizeMs,
      directResizeCount: this.directResizeCount,
      fallbackResizeCount: this.fallbackResizeCount,
      unchangedCount: this.unchangedCount,
    }
  }

  async decode(blob: Blob, signal?: AbortSignal): Promise<TexImageSource> {
    throwIfCancelled(signal, '(glTF 图片)')
    if (typeof createImageBitmap === 'function') {
      let encodedDimensions: EncodedImageDimensions | undefined
      if (Number.isFinite(this.maxSize)) {
        const headerStartedAt = clock.now()
        encodedDimensions = await readEncodedImageDimensions(blob)
        this.headerMs += clock.now() - headerStartedAt
      }
      throwIfCancelled(signal, '(glTF 图片)')
      const directDimensions = encodedDimensions
        ? getResizedDimensions(encodedDimensions.width, encodedDimensions.height, this.maxSize)
        : undefined
      if (directDimensions) {
        try {
          const directStartedAt = clock.now()
          let direct: ImageBitmap
          try {
            direct = await createImageBitmap(blob, {
              ...IMAGE_BITMAP_OPTIONS,
              resizeWidth: directDimensions[0],
              resizeHeight: directDimensions[1],
              resizeQuality: 'high',
            })
          } finally {
            this.directDecodeMs += clock.now() - directStartedAt
          }
          if (signal?.aborted) {
            direct.close()
            throw new MeshLoadCancelledError('(glTF 图片)', { cause: signal.reason })
          }
          if (direct.width === directDimensions[0] && direct.height === directDimensions[1]) {
            this.directResizeCount += 1
            return direct
          }
          direct.close()
        } catch (error) {
          if (error instanceof MeshLoadCancelledError) throw error
          // 浏览器不支持 Blob 直接缩放时，继续使用兼容回退路径。
        }
      }

      const fullDecodeStartedAt = clock.now()
      let bitmap: ImageBitmap
      try {
        bitmap = await createImageBitmap(blob, IMAGE_BITMAP_OPTIONS)
      } finally {
        this.fullDecodeMs += clock.now() - fullDecodeStartedAt
      }
      if (signal?.aborted) {
        bitmap.close()
        throw new MeshLoadCancelledError('(glTF 图片)', { cause: signal.reason })
      }
      const resizedDimensions = getResizedDimensions(bitmap.width, bitmap.height, this.maxSize)
      if (resizedDimensions) {
        let resized: ImageBitmap
        const resizeStartedAt = clock.now()
        try {
          resized = await createImageBitmap(bitmap, {
            ...IMAGE_BITMAP_OPTIONS,
            resizeWidth: resizedDimensions[0],
            resizeHeight: resizedDimensions[1],
            resizeQuality: 'high',
          })
        } catch (error) {
          bitmap.close()
          throw error
        } finally {
          this.resizeMs += clock.now() - resizeStartedAt
        }
        bitmap.close()
        if (signal?.aborted) {
          resized.close()
          throw new MeshLoadCancelledError('(glTF 图片)', { cause: signal.reason })
        }
        this.fallbackResizeCount += 1
        return resized
      }
      this.unchangedCount += 1
      return bitmap
    }

    const htmlDecodeStartedAt = clock.now()
    let htmlDecodeRecorded = false
    const recordHtmlDecode = () => {
      if (htmlDecodeRecorded) return
      htmlDecodeRecorded = true
      this.fullDecodeMs += clock.now() - htmlDecodeStartedAt
    }
    return new Promise<TexImageSource>((resolve, reject) => {
      const url = URL.createObjectURL(blob)
      const image = new Image()
      const cleanup = () => {
        URL.revokeObjectURL(url)
        signal?.removeEventListener('abort', handleAbort)
      }
      const handleAbort = () => {
        recordHtmlDecode()
        cleanup()
        reject(new MeshLoadCancelledError('(glTF 图片)', { cause: signal?.reason }))
      }
      image.onload = () => {
        recordHtmlDecode()
        cleanup()
        const resizedDimensions = getResizedDimensions(
          image.naturalWidth,
          image.naturalHeight,
          this.maxSize
        )
        if (!resizedDimensions) {
          this.unchangedCount += 1
          resolve(image)
          return
        }
        const resizeStartedAt = clock.now()
        const canvas = document.createElement('canvas')
        canvas.width = resizedDimensions[0]
        canvas.height = resizedDimensions[1]
        const context = canvas.getContext('2d')
        if (!context) {
          reject(new Error('glTF 图片降采样失败'))
          return
        }
        context.drawImage(image, 0, 0, canvas.width, canvas.height)
        this.resizeMs += clock.now() - resizeStartedAt
        this.fallbackResizeCount += 1
        resolve(canvas)
      }
      image.onerror = () => {
        recordHtmlDecode()
        cleanup()
        reject(new Error('glTF 图片解码失败'))
      }
      signal?.addEventListener('abort', handleAbort, { once: true })
      image.src = url
    })
  }
}

/** glTF core metallic-roughness 与 KHR_materials_unlit 的稳定材质适配器。 */
export class DefaultGltfCoreMaterialFactory implements GltfCoreMaterialFactory {
  private documentCaches = new WeakMap<GltfManifestDocument, GltfDocumentMaterialCaches>()
  private readonly createdMaterials = new Set<THREE.Material>()
  private readonly createdTextures = new Set<THREE.Texture>()
  private readonly imageLifetime = new ImageBitmapLifetime()
  private readonly loading: GltfLoadingOptions
  private readonly imageDecoder: GltfImageDecoder
  private imageCount = 0
  private embeddedImageCount = 0
  private externalImageCount = 0
  private dataUriImageCount = 0
  private encodedImageBytes = 0
  private imageReadMs = 0
  private imageDecodeMs = 0
  private readonly imageReadSamples: number[] = []
  private readonly imageDecodeSamples: number[] = []
  private slowestImageIndex: number | undefined
  private slowestImageDecodeMs = 0
  private slowestImageBytes = 0
  private slowestImageWidth = 0
  private slowestImageHeight = 0
  private estimatedTextureBytes = 0
  private activeImageReads = 0
  private peakConcurrentImageReads = 0
  private activeImageDecodes = 0
  private activeImageDecodeBytes = 0
  private peakConcurrentImages = 0
  private peakConcurrentImageBytes = 0
  private activeMaterialCreations = 0
  private readonly diagnosticsEnabled: boolean
  private disposeRequested = false
  private disposed = false

  constructor(
    private readonly request: MeshLoadRequest = {},
    imageDecoder?: GltfImageDecoder,
    diagnosticsEnabled = textureLog.isEnabled(LogLevel.DEBUG)
  ) {
    this.loading = resolveGltfLoadingOptions(undefined, request.gltf)
    this.imageDecoder =
      imageDecoder ?? new BrowserGltfImageDecoder(getConcreteGltfTextureMaxSize(this.loading))
    this.diagnosticsEnabled = diagnosticsEnabled
  }

  createMaterial(
    document: GltfManifestDocument,
    materialIndex: number | undefined,
    sources: readonly GltfBufferSource[],
    signal?: AbortSignal,
    features: GltfMaterialFeatures = { vertexColors: false, flatShading: false }
  ): Promise<THREE.MeshStandardMaterial | THREE.MeshBasicMaterial> {
    this.ensureActive()
    const cache = this.getDocumentCaches(document).materials
    const key = `${materialIndex ?? 'default'}:${features.vertexColors ? 1 : 0}:${features.flatShading ? 1 : 0}`
    const cached = cache.get(key)
    if (cached) return cached
    const promise = this.createMaterialTracked(document, materialIndex, sources, signal, features)
    cache.set(key, promise)
    promise.catch(() => {
      cache.delete(key)
    })
    return promise
  }

  getPerformanceReport(): GltfMaterialPerformanceReport {
    const decoding = this.imageDecoder.getPerformanceReport?.() ?? {
      decoderKind: 'custom' as const,
      headerMs: 0,
      directDecodeMs: 0,
      fullDecodeMs: 0,
      resizeMs: 0,
      directResizeCount: 0,
      fallbackResizeCount: 0,
      unchangedCount: 0,
    }
    return {
      ...decoding,
      imageCount: this.imageCount,
      embeddedImageCount: this.embeddedImageCount,
      externalImageCount: this.externalImageCount,
      dataUriImageCount: this.dataUriImageCount,
      encodedImageBytes: this.encodedImageBytes,
      imageReadMs: this.imageReadMs,
      imageReadP50Ms: percentile(this.imageReadSamples, 0.5),
      imageReadP95Ms: percentile(this.imageReadSamples, 0.95),
      imageReadMaxMs: Math.max(0, ...this.imageReadSamples),
      imageDecodeMs: this.imageDecodeMs,
      imageDecodeP50Ms: percentile(this.imageDecodeSamples, 0.5),
      imageDecodeP95Ms: percentile(this.imageDecodeSamples, 0.95),
      imageDecodeMaxMs: Math.max(0, ...this.imageDecodeSamples),
      slowestImageIndex: this.slowestImageIndex,
      slowestImageDecodeMs: this.slowestImageDecodeMs,
      slowestImageBytes: this.slowestImageBytes,
      slowestImageWidth: this.slowestImageWidth,
      slowestImageHeight: this.slowestImageHeight,
      estimatedTextureBytes: this.estimatedTextureBytes,
      peakConcurrentImageReads: this.peakConcurrentImageReads,
      peakConcurrentImages: this.peakConcurrentImages,
      peakConcurrentImageBytes: this.peakConcurrentImageBytes,
    }
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    for (const material of this.createdMaterials) material.dispose()
    for (const texture of this.createdTextures) texture.dispose()
    this.imageLifetime.dispose()
    this.createdMaterials.clear()
    this.createdTextures.clear()
    this.documentCaches = new WeakMap()
  }

  private async createMaterialUncached(
    document: GltfManifestDocument,
    materialIndex: number | undefined,
    sources: readonly GltfBufferSource[],
    signal: AbortSignal | undefined,
    features: GltfMaterialFeatures
  ): Promise<THREE.MeshStandardMaterial | THREE.MeshBasicMaterial> {
    throwIfCancelled(signal, document.source)
    const definition =
      materialIndex === undefined ? undefined : document.manifest.materials?.[materialIndex]
    assertSupportedMaterialExtensions(definition, materialIndex, document.source)
    const pbr = definition?.pbrMetallicRoughness
    const baseColor = pbr?.baseColorFactor ?? [1, 1, 1, 1]
    const emissive = definition?.emissiveFactor ?? [0, 0, 0]
    const alphaMode = definition?.alphaMode ?? 'OPAQUE'
    const commonParameters: THREE.MeshBasicMaterialParameters = {
      name:
        definition?.name ?? (materialIndex === undefined ? 'default' : `material-${materialIndex}`),
      color: new THREE.Color(baseColor[0] ?? 1, baseColor[1] ?? 1, baseColor[2] ?? 1),
      opacity: baseColor[3] ?? 1,
      side: definition?.doubleSided ? THREE.DoubleSide : THREE.FrontSide,
      transparent: alphaMode === 'BLEND',
      depthWrite: alphaMode !== 'BLEND',
      alphaTest: alphaMode === 'MASK' ? (definition?.alphaCutoff ?? 0.5) : 0,
      vertexColors: features.vertexColors,
    }
    const unlit = Object.hasOwn(definition?.extensions ?? {}, 'KHR_materials_unlit')
    const material = unlit
      ? new THREE.MeshBasicMaterial(commonParameters)
      : new THREE.MeshStandardMaterial({
          ...commonParameters,
          metalness: pbr?.metallicFactor ?? 1,
          roughness: pbr?.roughnessFactor ?? 1,
          emissive: new THREE.Color(emissive[0] ?? 0, emissive[1] ?? 0, emissive[2] ?? 0),
          flatShading: features.flatShading,
        })
    this.createdMaterials.add(material)

    if (pbr?.baseColorTexture) {
      material.map = await this.getTexture(
        document,
        pbr.baseColorTexture,
        THREE.SRGBColorSpace,
        sources,
        signal
      )
    }
    if (material instanceof THREE.MeshStandardMaterial && pbr?.metallicRoughnessTexture) {
      const texture = await this.getTexture(
        document,
        pbr.metallicRoughnessTexture,
        THREE.NoColorSpace,
        sources,
        signal
      )
      material.metalnessMap = texture
      material.roughnessMap = texture
    }
    if (material instanceof THREE.MeshStandardMaterial && definition?.normalTexture) {
      material.normalMap = await this.getTexture(
        document,
        definition.normalTexture,
        THREE.NoColorSpace,
        sources,
        signal
      )
      const scale = definition.normalTexture.scale ?? 1
      material.normalScale.set(scale, scale)
    }
    if (material instanceof THREE.MeshStandardMaterial && definition?.occlusionTexture) {
      material.aoMap = await this.getTexture(
        document,
        definition.occlusionTexture,
        THREE.NoColorSpace,
        sources,
        signal
      )
      material.aoMapIntensity = definition.occlusionTexture.strength ?? 1
    }
    if (material instanceof THREE.MeshStandardMaterial && definition?.emissiveTexture) {
      material.emissiveMap = await this.getTexture(
        document,
        definition.emissiveTexture,
        THREE.SRGBColorSpace,
        sources,
        signal
      )
    }
    throwIfCancelled(signal, document.source)
    material.needsUpdate = true
    return material
  }

  private async createMaterialTracked(
    document: GltfManifestDocument,
    materialIndex: number | undefined,
    sources: readonly GltfBufferSource[],
    signal: AbortSignal | undefined,
    features: GltfMaterialFeatures
  ): Promise<THREE.MeshStandardMaterial | THREE.MeshBasicMaterial> {
    this.activeMaterialCreations += 1
    try {
      return await this.createMaterialUncached(document, materialIndex, sources, signal, features)
    } catch (error) {
      this.disposeRequested = true
      throw error
    } finally {
      this.activeMaterialCreations -= 1
      if (this.disposeRequested && this.activeMaterialCreations === 0) this.dispose()
    }
  }

  private getTexture(
    document: GltfManifestDocument,
    textureInfo: GltfTextureInfo,
    colorSpace: THREE.ColorSpace,
    sources: readonly GltfBufferSource[],
    signal?: AbortSignal
  ): Promise<THREE.Texture> {
    const textureIndex = textureInfo.index
    const channel = textureInfo.texCoord ?? 0
    const definition = document.manifest.textures?.[textureIndex]
    const key = `${definition?.source ?? 'missing'}:${definition?.sampler ?? 'default'}:${colorSpace}:${channel}`
    const cache = this.getDocumentCaches(document).textures
    const cached = cache.get(key)
    if (cached) return cached
    const promise = this.createTexture(document, textureIndex, colorSpace, channel, sources, signal)
    cache.set(key, promise)
    promise.catch(() => cache.delete(key))
    return promise
  }

  private async createTexture(
    document: GltfManifestDocument,
    textureIndex: number,
    colorSpace: THREE.ColorSpace,
    channel: number,
    sources: readonly GltfBufferSource[],
    signal?: AbortSignal
  ): Promise<THREE.Texture> {
    const definition = document.manifest.textures?.[textureIndex]
    if (!definition || definition.source === undefined) {
      throw new GltfDirectBuildError(document.source, `textures[${textureIndex}] 缺少 source`)
    }
    const extensions = Object.keys(definition.extensions ?? {})
    if (extensions.length > 0) {
      throw new GltfLowMemoryUnsupportedError(
        document.source,
        'unsupported-material-extension',
        `textures[${textureIndex}]:${extensions.join(',')}`
      )
    }
    const image = await this.getImage(document, definition.source, sources, signal)
    const sampler =
      definition.sampler === undefined
        ? undefined
        : document.manifest.samplers?.[definition.sampler]
    const minFilter = getMinFilter(sampler?.minFilter)
    const textureBytes = estimateGltfTextureBytes(image, minFilter)
    const cache = this.getDocumentCaches(document)
    const nextTextureBytes = cache.estimatedTextureBytes + textureBytes
    if (nextTextureBytes > this.loading.budget.maxTextureBytes) {
      this.imageLifetime.closeIfUnreferenced(image)
      throw new GltfMemoryBudgetExceededError(
        document.source,
        'budget-texture-exceeded',
        nextTextureBytes,
        this.loading.budget.maxTextureBytes
      )
    }
    cache.estimatedTextureBytes = nextTextureBytes
    this.estimatedTextureBytes += textureBytes
    const texture = new THREE.Texture(image)
    texture.name = definition.name ?? `texture-${textureIndex}`
    texture.flipY = false
    texture.colorSpace = colorSpace
    texture.channel = channel
    texture.wrapS = getWrap(sampler?.wrapS)
    texture.wrapT = getWrap(sampler?.wrapT)
    texture.magFilter = getMagFilter(sampler?.magFilter)
    texture.minFilter = minFilter
    texture.needsUpdate = true
    this.imageLifetime.bind(texture, image)
    this.createdTextures.add(texture)
    return texture
  }

  private getImage(
    document: GltfManifestDocument,
    imageIndex: number,
    sources: readonly GltfBufferSource[],
    signal?: AbortSignal
  ): Promise<TexImageSource> {
    const cache = this.getDocumentCaches(document).images
    const cached = cache.get(imageIndex)
    if (cached) return cached
    const promise = this.loadImage(document, imageIndex, sources, signal)
    cache.set(imageIndex, promise)
    promise.catch(() => cache.delete(imageIndex))
    return promise
  }

  private async loadImage(
    document: GltfManifestDocument,
    imageIndex: number,
    sources: readonly GltfBufferSource[],
    signal?: AbortSignal
  ): Promise<TexImageSource> {
    const image = document.manifest.images?.[imageIndex]
    if (!image) {
      throw new GltfDirectBuildError(document.source, `images[${imageIndex}] 不存在`)
    }
    const imageSourceKind = image.uri
      ? image.uri.startsWith('data:')
        ? 'data-uri'
        : 'external'
      : 'embedded'
    const readStartedAt = clock.now()
    this.activeImageReads += 1
    this.peakConcurrentImageReads = Math.max(this.peakConcurrentImageReads, this.activeImageReads)
    let blob: Blob
    try {
      blob = await this.loadImageBlob(document, image, sources, signal)
    } finally {
      this.activeImageReads -= 1
    }
    const readMs = clock.now() - readStartedAt
    throwIfCancelled(signal, document.source)
    const decodeStartedAt = clock.now()
    const maxSize = getConcreteGltfTextureMaxSize(this.loading)
    const decodedReservationBytes = Number.isFinite(maxSize) ? maxSize * maxSize * 4 : 0
    const activeBytes = blob.size + decodedReservationBytes
    this.activeImageDecodes += 1
    this.activeImageDecodeBytes += activeBytes
    this.peakConcurrentImages = Math.max(this.peakConcurrentImages, this.activeImageDecodes)
    this.peakConcurrentImageBytes = Math.max(
      this.peakConcurrentImageBytes,
      this.activeImageDecodeBytes
    )
    let decoded: TexImageSource
    try {
      decoded = await this.imageDecoder.decode(blob, signal)
    } finally {
      this.activeImageDecodes -= 1
      this.activeImageDecodeBytes -= activeBytes
    }
    const decodeMs = clock.now() - decodeStartedAt
    if (signal?.aborted) {
      this.imageLifetime.closeIfUnreferenced(decoded)
      throw new MeshLoadCancelledError(document.source, { cause: signal.reason })
    }
    this.imageLifetime.track(decoded)
    const [width, height] = getImageDimensions(decoded)
    this.imageCount += 1
    if (imageSourceKind === 'embedded') this.embeddedImageCount += 1
    else if (imageSourceKind === 'external') this.externalImageCount += 1
    else this.dataUriImageCount += 1
    this.encodedImageBytes += blob.size
    this.imageReadMs += readMs
    if (this.diagnosticsEnabled) this.imageReadSamples.push(readMs)
    this.imageDecodeMs += decodeMs
    if (this.diagnosticsEnabled) this.imageDecodeSamples.push(decodeMs)
    if (this.slowestImageIndex === undefined || decodeMs > this.slowestImageDecodeMs) {
      this.slowestImageIndex = imageIndex
      this.slowestImageDecodeMs = decodeMs
      this.slowestImageBytes = blob.size
      this.slowestImageWidth = width
      this.slowestImageHeight = height
    }
    return decoded
  }

  private async loadImageBlob(
    document: GltfManifestDocument,
    image: GltfImage,
    sources: readonly GltfBufferSource[],
    signal?: AbortSignal
  ): Promise<Blob> {
    if (image.uri?.startsWith('data:')) return dataUriToBlob(image.uri)
    if (image.uri) {
      const response = await fetch(resolveImageUrl(image.uri, document.baseUrl), {
        signal,
        headers: this.request.headers,
        credentials: this.request.credentials,
      })
      if (!response.ok) {
        throw new GltfDirectBuildError(
          document.source,
          `图片请求失败: ${response.status} ${response.statusText}`
        )
      }
      const bytes = await response.arrayBuffer()
      throwIfCancelled(signal, document.source)
      return new Blob([bytes], {
        type: response.headers.get('content-type') ?? image.mimeType ?? 'application/octet-stream',
      })
    }
    if (image.bufferView === undefined) {
      throw new GltfDirectBuildError(document.source, '图片缺少 uri 或 bufferView')
    }
    const bufferView = document.manifest.bufferViews?.[image.bufferView]
    if (!bufferView) {
      throw new GltfDirectBuildError(document.source, `图片 bufferView 不存在: ${image.bufferView}`)
    }
    const source = sources[bufferView.buffer]
    if (!source) {
      throw new GltfDirectBuildError(
        document.source,
        `图片 BufferSource 不存在: ${bufferView.buffer}`
      )
    }
    const bytes = await source.read(bufferView.byteOffset ?? 0, bufferView.byteLength, signal)
    return new Blob([bytes.slice().buffer as ArrayBuffer], {
      type: image.mimeType ?? 'application/octet-stream',
    })
  }

  private ensureActive(): void {
    if (this.disposed) throw new GltfDirectBuildError('(材质工厂)', '材质工厂已释放')
  }

  private getDocumentCaches(document: GltfManifestDocument): GltfDocumentMaterialCaches {
    let caches = this.documentCaches.get(document)
    if (!caches) {
      caches = createDocumentCaches()
      this.documentCaches.set(document, caches)
    }
    return caches
  }
}
